import Phaser from 'phaser';
import type { Side } from '../game/world/types';

/**
 * Module-level singleton event bus bridging Phaser scene state to the
 * parallel accessible DOM layer (`AccessibilityLayer.ts`). Deliberately NOT
 * scene-bound (unlike `this.events` on a `Phaser.Scene`, which only reaches
 * listeners attached to that same scene instance, or the Phaser game/canvas
 * itself) - the DOM accessibility layer lives entirely outside Phaser's
 * scene tree (real `<button>` elements + an `aria-live` region in
 * `index.html`), so it needs a channel neither a scene nor `document`
 * naturally exposes.
 *
 * CP22 fix (Vera's CRITICAL accessibility finding, `game-qa-report.md`):
 * canvas-only rendering exposed zero DOM/ARIA surface to assistive tech.
 * This bus is the wiring between Phaser gameplay state (WorldScene/HudScene)
 * and the real, keyboard- and screen-reader-operable controls + live region
 * mounted by `mountAccessibilityLayer()`.
 */
export const a11yBus = new Phaser.Events.EventEmitter();

export const A11Y_EVENTS = {
  /** Fired once per session boot (HudScene.create): side, quota, split-bar %. */
  SESSION_START: 'a11y:sessionStart',
  /** Fired whenever a tile resolves (WorldScene.resolveActiveTile). */
  TILE_RESOLVED: 'a11y:tileResolved',
  /** Fired for toast-worthy messages (e.g. quota exhausted). */
  TOAST: 'a11y:toast',
  /** DOM button -> scene: movement hold state, payload is `boolean`. */
  MOVE_LEFT: 'a11y:moveLeft',
  MOVE_RIGHT: 'a11y:moveRight',
  /** DOM button -> scene: single chop/plant attempt, no payload. */
  ACT: 'a11y:act',
} as const;

export interface SessionStartDetail {
  side: Side;
  quota: number;
  beorningsPct: number;
  lothwenPct: number;
}

export interface TileResolvedDetail {
  side: Side;
  frontierPosition: number;
  quotaRemaining: number;
  actionsThisSession: number;
}
